import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Loader2, LogIn, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_grupo-consolidado/artifacts/xo8xf0yu_GRUPO%20CEAP%20BRASIL%20%281%29.png';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Se já está logado, manda direto pra home
  React.useEffect(() => {
    if (user) {
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [user, navigate, location.state]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast.error('Preencha email e senha');
      return;
    }
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      toast.success('Login realizado');
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erro ao autenticar';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-zinc-950 relative overflow-hidden">
      {/* Background com textura sutil */}
      <div
        className="absolute inset-0 opacity-30"
        style={{
          background: 'radial-gradient(ellipse 60% 50% at 30% 30%, rgba(212,175,55,0.15), transparent 60%), radial-gradient(ellipse 50% 40% at 70% 70%, rgba(124,58,237,0.08), transparent 60%)',
          pointerEvents: 'none',
        }}
      />
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
          backgroundSize: '50px 50px',
          pointerEvents: 'none',
        }}
      />

      <div className="relative z-10 w-full max-w-md px-6">
        {/* LOGO */}
        <div className="flex flex-col items-center mb-7">
          <div className="w-28 h-28 rounded-full bg-zinc-950 border border-amber-700/30 flex items-center justify-center mb-5 shadow-2xl shadow-amber-900/10" style={{ overflow: 'hidden' }}>
            <img src={LOGO_URL} alt="Grupo CEAP BRASIL" className="w-full h-full object-contain p-1" data-testid="login-logo" />
          </div>
          <h1 className="font-display text-2xl md:text-3xl text-center text-zinc-50 tracking-tight" data-testid="login-title">
            Seja Bem vindo ao
          </h1>
          <h2 className="font-display text-base md:text-lg text-center gold-text mt-1">
            Sistema de Gestão Financeira
          </h2>
          <div className="text-[11px] text-zinc-500 uppercase tracking-[0.25em] mt-3" data-testid="login-subtitle">
            Grupo CEAP BRASIL
          </div>
        </div>

        {/* CARD */}
        <form
          onSubmit={onSubmit}
          className="bg-zinc-900/40 backdrop-blur-sm border border-zinc-800 rounded-lg px-7 py-7 shadow-2xl"
          data-testid="login-form"
        >
          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-zinc-400 text-[11px] uppercase tracking-wider">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seuemail@ceapbrasil.com"
                className="bg-zinc-950 border-zinc-800 mt-1.5 text-zinc-100 placeholder:text-zinc-600 h-10"
                autoFocus
                autoComplete="email"
                data-testid="login-email"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-zinc-400 text-[11px] uppercase tracking-wider">Senha</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPass ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="bg-zinc-950 border-zinc-800 mt-1.5 text-zinc-100 placeholder:text-zinc-600 h-10 pr-10"
                  autoComplete="current-password"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-2 top-2 p-1.5 text-zinc-500 hover:text-zinc-300"
                  aria-label="Mostrar/ocultar senha"
                >
                  {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="btn-gold w-full mt-6 gap-2 h-11 text-sm font-semibold"
            data-testid="login-submit"
          >
            {submitting ? <Loader2 className="animate-spin" size={15} /> : <LogIn size={15} />}
            {submitting ? 'Autenticando...' : 'Entrar'}
          </Button>
        </form>

        <div className="text-center text-[11px] text-zinc-600 mt-5">
          Acesso restrito · Para suporte: administrativo@ceapbrasil.com
        </div>
      </div>
    </div>
  );
}
